import { Helmet } from "react-helmet-async";
import React from "react";
import { useTranslation } from "react-i18next";
import autogenerateSpaceTitle from "helpers/autogenerateSpaceTitle";
import { getStreetName } from "views/SpacePage/helpers";

// Helper function to get correct base URL based on current domain
const getBaseUrl = () => {
  if (typeof window === 'undefined') return 'https://chooseandwork.com';
  
  const hostname = window.location.hostname;
  const cleanHost = hostname.replace(/^www\./, '');
  
  if (cleanHost.includes('chooseandwork.be')) return 'https://chooseandwork.be';
  if (cleanHost.includes('chooseandwork.fr')) return 'https://chooseandwork.fr';
  return 'https://chooseandwork.com';
};

// Helper to determine language and region
const getLanguageInfo = (language, hostname) => {
  const isBeDomain = hostname.includes('chooseandwork.be');
  const isFrDomain = hostname.includes('chooseandwork.fr');
  
  if (isFrDomain) return { hreflang: 'fr-fr', ogLocale: 'fr_FR' };
  if (isBeDomain) return { hreflang: 'fr-be', ogLocale: 'fr_BE' };
  return { hreflang: 'en', ogLocale: 'en_US' };
};

const SpacePageHelmet = ({ spaceData, language, spaceStatus }) => {
  const { t } = useTranslation("global");

  if (!spaceData) return null;

  // Get domain-specific data
  const baseUrl = getBaseUrl();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const languageInfo = getLanguageInfo(language, hostname);
  
  // Build canonical URL based on current domain
  const canonicalUrl = `${baseUrl}${currentPath}`;
  
  // Only index approved spaces
  const shouldIndex = spaceStatus === 'approved' || spaceStatus === 'active' || spaceStatus === 'published';
  
  // SEO Meta Data
  const seoTitle = spaceData?.meta_title || autogenerateSpaceTitle(spaceData, t) || "Choose&Work";
  const seoDescription = spaceData?.meta_description || 
    spaceData?.description?.replace(/<[^>]*>/g, '').substring(0, 160) || 
    t("space.defaultMetaDescription");
  const seoKeywords = spaceData?.meta_keywords || 
    `${spaceData?.placeUsage?.name}, ${spaceData?.address?.city}, ${spaceData?.place_style?.name}`;
  
  const ogImage = spaceData?.images?.[0]?.image_url || `${baseUrl}/og-image-space.jpg`;
  const twitterImage = spaceData?.images?.[0]?.image_url || `${baseUrl}/twitter-image-space.jpg`;

  // Helper function to convert currency code
  const getCurrencyCode = (currency) => {
    if (currency === "978") return "EUR";
    if (currency === "826") return "GBP";
    if (currency === "840") return "USD";
    return currency || "EUR";
  };

  // Get currency symbol for priceRange
  const getCurrencySymbol = (currencyCode) => {
    const symbols = { EUR: '€', GBP: '£', USD: '$', CHF: 'Fr' };
    return symbols[currencyCode] || '€';
  };

  // Get minimum price
  const getMinPrice = () => {
    const prices = [];
    if (spaceData?.price_hour && parseFloat(spaceData.price_hour) > 0) prices.push(parseFloat(spaceData.price_hour));
    if (spaceData?.price_half_day && parseFloat(spaceData.price_half_day) > 0) prices.push(parseFloat(spaceData.price_half_day));
    if (spaceData?.price_day && parseFloat(spaceData.price_day) > 0) prices.push(parseFloat(spaceData.price_day));
    if (spaceData?.price_week && parseFloat(spaceData.price_week) > 0) prices.push(parseFloat(spaceData.price_week));
    if (spaceData?.price_month && parseFloat(spaceData.price_month) > 0) prices.push(parseFloat(spaceData.price_month));
    
    if (prices.length > 0) return Math.min(...prices);
    return spaceData?.price ? parseFloat(spaceData.price) : 0;
  };

  // Get maximum price
  const getMaxPrice = () => {
    const prices = [];
    if (spaceData?.price_hour && parseFloat(spaceData.price_hour) > 0) prices.push(parseFloat(spaceData.price_hour));
    if (spaceData?.price_half_day && parseFloat(spaceData.price_half_day) > 0) prices.push(parseFloat(spaceData.price_half_day));
    if (spaceData?.price_day && parseFloat(spaceData.price_day) > 0) prices.push(parseFloat(spaceData.price_day));
    if (spaceData?.price_week && parseFloat(spaceData.price_week) > 0) prices.push(parseFloat(spaceData.price_week));
    if (spaceData?.price_month && parseFloat(spaceData.price_month) > 0) prices.push(parseFloat(spaceData.price_month));
    
    if (prices.length > 0) return Math.max(...prices);
    return spaceData?.price ? parseFloat(spaceData.price) * 10 : 100;
  };

  // Format price range correctly for Google
  const getFormattedPriceRange = () => {
    const minPrice = getMinPrice();
    const maxPrice = getMaxPrice();
    const currencyCode = getCurrencyCode(spaceData.currency);
    const symbol = getCurrencySymbol(currencyCode);
    
    if (minPrice === maxPrice || maxPrice === 0 || minPrice === 0) {
      return `${symbol}${minPrice || maxPrice || 0}`;
    }
    return `${symbol}${minPrice} - ${symbol}${maxPrice}`;
  };

  // Build alternate URLs
  const getAlternateUrls = () => {
    const alternates = [];
    const basePath = currentPath;
    
    alternates.push({ hreflang: 'en', url: `https://chooseandwork.com${basePath}` });
    alternates.push({ hreflang: 'fr-fr', url: `https://chooseandwork.fr${basePath}` });
    alternates.push({ hreflang: 'fr-be', url: `https://chooseandwork.be${basePath}` });
    alternates.push({ hreflang: 'x-default', url: `https://chooseandwork.com${basePath}` });
    
    return alternates;
  };

  // Get day of week for opening hours
  const getDayOfWeek = (dayNumber) => {
    const days = { 1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday" };
    return days[dayNumber];
  };

  // Build opening hours specification
  const getOpeningHoursSpecification = () => {
    if (spaceData?.opening_hours && Array.isArray(spaceData.opening_hours) && spaceData.opening_hours.length > 0) {
      const validHours = spaceData.opening_hours.filter(hour => hour.day && hour.from && hour.to);
      
      if (validHours.length > 0) {
        return validHours.map(hour => ({
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": getDayOfWeek(hour.day),
          "opens": hour.from.substring(0, 5),
          "closes": hour.to.substring(0, 5)
        }));
      }
    }
    
    return [{
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }];
  };

  // Build offers with all required fields - FIXED VERSION
  const buildOffers = () => {
    const offers = [];
    const currencyCode = getCurrencyCode(spaceData.currency);
    
    // Ensure availability is always a valid URL string
    const availability = spaceData?.is_available !== false 
      ? "https://schema.org/InStock" 
      : "https://schema.org/OutOfStock";
    
    const validFrom = new Date().toISOString().split('T')[0];
    const validThrough = new Date();
    validThrough.setFullYear(validThrough.getFullYear() + 1);
    const validThroughDate = validThrough.toISOString().split('T')[0];

    const createOffer = (price, name, unitCode = null) => {
      // Validate price - must be a positive number
      const priceValue = parseFloat(price);
      if (!price || isNaN(priceValue) || priceValue <= 0) return null;
      
      const offer = {
        "@type": "Offer",
        "name": name,
        "price": priceValue,
        "priceCurrency": currencyCode,
        "availability": availability,
        "validFrom": validFrom,
        "validThrough": validThroughDate,
        "url": canonicalUrl,
        "priceSpecification": {
          "@type": unitCode ? "UnitPriceSpecification" : "PriceSpecification",
          "price": priceValue,
          "priceCurrency": currencyCode
        }
      };
      
      // Only add unitCode if it exists
      if (unitCode) {
        offer.priceSpecification.unitCode = unitCode;
      }
      
      return offer;
    };

    // Create offers for each price type
    const hourlyOffer = createOffer(spaceData?.price_hour, t("space.byHourFull") || "Hourly rate", "HUR");
    if (hourlyOffer) offers.push(hourlyOffer);
    
    const halfDayOffer = createOffer(spaceData?.price_half_day, t("space.byHalfDayFull") || "Half-day rate");
    if (halfDayOffer) offers.push(halfDayOffer);
    
    const dailyOffer = createOffer(spaceData?.price_day, t("space.byDayFull") || "Daily rate", "DAY");
    if (dailyOffer) offers.push(dailyOffer);
    
    const weeklyOffer = createOffer(spaceData?.price_week, t("space.byWeekFull") || "Weekly rate", "WEE");
    if (weeklyOffer) offers.push(weeklyOffer);
    
    const monthlyOffer = createOffer(spaceData?.price_month, t("space.byMonthFull") || "Monthly rate", "MON");
    if (monthlyOffer) offers.push(monthlyOffer);

    // Return undefined instead of empty array for cleaner JSON-LD
    return offers.length > 0 ? offers : undefined;
  };

  // Get structured data type
  const getStructuredDataType = () => {
    const usageKey = spaceData?.placeUsage?.key;
    switch(usageKey) {
      case "coworking": return "CoworkingSpace";
      case "conference_room": return "MeetingRoom";
      case "private_office": return "Office";
      case "teambuilding":
      case "seminar": return "EventVenue";
      default: return "LocalBusiness";
    }
  };

  // Get aggregate rating with validation
  const getAggregateRating = () => {
    if (!spaceData?.note_avg || spaceData.note_avg === 0) return undefined;
    
    let ratingValue = parseFloat(spaceData.note_avg);
    if (ratingValue > 5 && ratingValue <= 10) ratingValue = ratingValue / 2;
    ratingValue = Math.min(5, Math.max(1, ratingValue));
    
    return {
      "@type": "AggregateRating",
      "ratingValue": ratingValue.toFixed(1),
      "reviewCount": spaceData?.review_count || 1,
      "bestRating": "5",
      "worstRating": "1"
    };
  };

  // Get additional properties
  const getAdditionalProperties = () => {
    const props = [];
    if (spaceData?.placeUsage?.name) props.push({ "@type": "PropertyValue", "name": "Space Type", "value": spaceData.placeUsage.name });
    if (spaceData?.desk && parseFloat(spaceData.desk) > 0) props.push({ "@type": "PropertyValue", "name": "Capacity", "value": `${spaceData.desk} people` });
    if (spaceData?.area && parseFloat(spaceData.area) > 0) props.push({ "@type": "PropertyValue", "name": "Area", "value": `${spaceData.area} m²` });
    if (spaceData?.place_style?.name) props.push({ "@type": "PropertyValue", "name": "Style", "value": spaceData.place_style.name });
    return props.length > 0 ? props : undefined;
  };

  // Get amenities
  const getAmenities = () => {
    const amenities = [];
    const freeServices = spaceData?.services?.filter(s => s.place_service?.type === "free" && s.available);
    if (freeServices?.length > 0) {
      freeServices.forEach(service => {
        if (service.place_service?.name) amenities.push(service.place_service.name);
      });
    }
    return amenities.length > 0 ? amenities : undefined;
  };

  // Clean structured data (remove empty values)
  const cleanStructuredData = (obj) => {
    if (!obj) return null;
    
    const cleanObj = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value === undefined || value === null) return;
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return;
      
      if (typeof value === 'object' && !Array.isArray(value)) {
        const cleaned = cleanStructuredData(value);
        if (cleaned && Object.keys(cleaned).length > 0) cleanObj[key] = cleaned;
        return;
      }
      
      if (Array.isArray(value)) {
        const cleanedArray = value.map(item => {
          if (typeof item === 'object' && item !== null) return cleanStructuredData(item);
          return item;
        }).filter(item => item !== undefined && item !== null && item !== '');
        
        if (cleanedArray.length > 0) cleanObj[key] = cleanedArray;
        return;
      }
      
      if (typeof value === 'string' && value.trim() === '') return;
      cleanObj[key] = value;
    });
    
    return cleanObj;
  };

  // Validate structured data before output - FIXED VERSION
  const validateStructuredData = (data) => {
    if (!data) return null;
    
    // Check required top-level fields
    const requiredFields = ['@context', '@type', 'name', 'url'];
    const missingFields = requiredFields.filter(field => !data[field]);
    if (missingFields.length > 0) {
      console.warn(`[Structured Data] Missing required fields: ${missingFields.join(', ')}`);
      return null;
    }
    
    // Check location exists
    if (!data.location) {
      console.warn('[Structured Data] Missing location property');
      return null;
    }
    
    // Check geo inside location - critical
    if (!data.location.geo || !data.location.geo.latitude || !data.location.geo.longitude) {
      console.warn('[Structured Data] Missing geo coordinates inside location');
      return null;
    }
    
    // Validate offers if present
    if (data.offers && Array.isArray(data.offers)) {
      const invalidOffers = data.offers.filter(offer => {
        return !offer.price || !offer.priceCurrency || !offer.availability;
      });
      
      if (invalidOffers.length > 0) {
        console.warn(`[Structured Data] ${invalidOffers.length} offer(s) missing required fields (price, priceCurrency, or availability)`);
        // Don't return null - some offers may still be valid
      }
    }
    
    return data;
  };

  // Generate structured data
  const generateStructuredData = () => {
    if (!shouldIndex) return null;
    
    const name = autogenerateSpaceTitle(spaceData, t);
    const description = spaceData?.description?.replace(/<[^>]*>/g, '').substring(0, 500) || "Professional workspace available for booking";
    const images = spaceData?.images?.map(img => img.image_url).filter(url => url) || [];
    const streetAddress = getStreetName(spaceData?.address?.address) || "Address not specified";
    const addressLocality = spaceData?.address?.city || "City not specified";
    const postalCode = spaceData?.address?.postal_code || "00000";
    const addressCountry = spaceData?.address?.country || "France";
    const latitude = spaceData?.address?.lat ? parseFloat(spaceData.address.lat) : null;
    const longitude = spaceData?.address?.lng ? parseFloat(spaceData.address.lng) : null;
    
    // Don't output if missing critical geo data
    if (!latitude || !longitude) {
      console.warn('[Structured Data] Missing geo coordinates, skipping schema');
      return null;
    }
    
    return {
      "@context": "https://schema.org",
      "@type": getStructuredDataType(),
      "name": name,
      "description": description,
      "url": canonicalUrl,
      "image": images,
      "telephone": spaceData?.phone || undefined,
      "email": spaceData?.email || undefined,
      "priceRange": getFormattedPriceRange(),
      "location": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": streetAddress,
          "addressLocality": addressLocality,
          "addressRegion": spaceData?.address?.state || undefined,
          "postalCode": postalCode,
          "addressCountry": addressCountry
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": latitude,
          "longitude": longitude
        }
      },
      "openingHoursSpecification": getOpeningHoursSpecification(),
      "offers": buildOffers(),
      "aggregateRating": getAggregateRating(),
      "amenityFeature": getAmenities()?.map(amenity => ({
        "@type": "LocationFeatureSpecification",
        "name": amenity,
        "value": true
      })),
      "additionalProperty": getAdditionalProperties(),
      "areaServed": addressLocality !== "City not specified" ? { 
        "@type": "City", 
        "name": addressLocality 
      } : undefined
    };
  };

  // Generate and validate structured data
  const rawStructuredData = generateStructuredData();
  const cleanedStructuredData = cleanStructuredData(rawStructuredData);
  const finalStructuredData = validateStructuredData(cleanedStructuredData);
  const alternateUrls = getAlternateUrls();

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      {seoKeywords && <meta name="keywords" content={seoKeywords} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* HREFLANG tags for international SEO */}
      {alternateUrls.map((alt) => (
        <link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.url} />
      ))}
      
      {/* Robots meta tag */}
      <meta name="robots" content={shouldIndex ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' : 'noindex, nofollow'} />
      
      {/* Open Graph tags */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={languageInfo.ogLocale} />
      <meta property="og:site_name" content="Choose&Work" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={twitterImage} />
      <meta name="twitter:site" content="@chooseandwork" />
      
      {/* Additional SEO tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content={languageInfo.hreflang} />
      
      {/* JSON-LD Structured Data */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData, null, 2)}
        </script>
      )}
    </Helmet>
  );
};

export default SpacePageHelmet;